import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * CinematicScroll — scroll-pinned crossfade hero.
 *
 * Implementation notes:
 *  - Uses a plain `window.scroll` listener (NOT framer's useScroll) because
 *    useScroll({ target: ref, ... }) was failing to hydrate, causing scroll
 *    progress to plateau at ~0.4 and the last few slides to never appear.
 *  - The section is `n * 110vh` tall with an inner `sticky top-0 h-screen` viewport.
 *  - Active index = floor(progressInsideSection * n). Each image gets EXACTLY 1/n
 *    of the scrollable distance — so all 6 are guaranteed to be reached.
 *  - <AnimatePresence mode="popLayout"> crossfades the active slide with
 *    zoom-in entry (scale 1.18 → 1.0) and zoom-out exit (1.0 → 0.85).
 */
export default function CinematicScroll({ items = [] }) {
  const ref = useRef(null);
  const visible = items.slice(0, 6);
  const total = Math.max(1, visible.length);
  const [active, setActive] = useState(0);
  const [scrollLabel, setScrollLabel] = useState(0); // 0..1 progress for the small "Scroll" indicator label

  const compute = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewport = window.innerHeight || document.documentElement.clientHeight;
    const sectionHeight = el.offsetHeight;
    const travel = Math.max(1, sectionHeight - viewport);
    // distance scrolled into the section
    const scrolled = -rect.top; // when section top is above viewport top, this is positive
    const progress = Math.max(0, Math.min(1, scrolled / travel));
    const idx = Math.min(total - 1, Math.floor(progress * total * 0.9999));
    setActive(idx);
    setScrollLabel(progress);
  }, [total]);

  useEffect(() => {
    compute();
    const onScroll = () => compute();
    const onResize = () => compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [compute]);

  if (!visible.length) {
    return <section className="h-[60vh] flex items-center justify-center text-ahm-teal/60">Loading…</section>;
  }

  const current = visible[active] || visible[0];

  return (
    <section
      ref={ref}
      style={{ height: `${total * 110}vh` }}
      className="relative bg-ahm-cream"
      data-testid="hero-cinematic"
      data-active-index={active}
      data-progress={scrollLabel.toFixed(3)}
    >
      <div className="sticky top-0 h-screen overflow-hidden">
        {/* Editorial copy layer */}
        <div className="absolute inset-0 pointer-events-none z-10">
          <div className="max-w-7xl mx-auto h-full px-4 md:px-8 lg:px-12 flex flex-col justify-between py-20 md:py-28">
            <div>
              <p className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-ahm-teal/70 font-semibold">
                Arkham House Media · Belgaum
              </p>
              <h1 className="font-display text-[11vw] md:text-[7vw] lg:text-[5.5vw] leading-[0.88] tracking-tight text-ahm-teal mt-4 md:mt-5 max-w-4xl">
                We build <em className="not-italic text-ahm-orange">brands</em> worth <em className="not-italic text-ahm-orange">obsessing</em> over.
              </h1>
            </div>
            <div className="flex items-end justify-between gap-6">
              <p className="max-w-md text-xs sm:text-sm md:text-base lg:text-lg text-ahm-teal/80 leading-relaxed">
                Independent creative studio. Brand films, social campaigns and identity work — for ambitious founders.
              </p>
              <div className="hidden md:flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-ahm-teal/55 font-semibold">
                <span>Scroll</span>
                <span className="block w-10 h-px bg-ahm-teal/55" />
                <span>{String(active + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Crossfading slide */}
        <div className="absolute inset-0 flex items-center justify-center">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              key={current.id || `slide-${active}`}
              data-testid={`cinematic-slide-${active}`}
              initial={{ opacity: 0, scale: 1.18, y: "2vh" }}
              animate={{ opacity: 1, scale: 1.0, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: "-2vh" }}
              transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
              className="absolute"
            >
              <div className="relative w-[88vw] h-[68vh] sm:w-[82vw] sm:h-[72vh] md:w-[58vw] md:h-[78vh] lg:w-[52vw] lg:h-[80vh] rounded-[24px] md:rounded-[40px] overflow-hidden shadow-[0_30px_80px_-20px_rgba(8,71,52,0.45)] ring-1 ring-ahm-teal/15">
                <img src={current.image} alt={current.label || ""} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ahm-teal/55 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 md:bottom-8 md:left-8 text-ahm-cream max-w-[80%]">
                  <p className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-ahm-yellow">
                    {current.tag} · Chapter
                  </p>
                  <h3 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[0.95] mt-2">
                    {current.label}
                  </h3>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Progress indicator */}
        <div className="absolute right-3 md:right-6 bottom-6 md:bottom-10 z-20 flex flex-col gap-2">
          {visible.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 transition-all duration-500 ${
                i === active ? "w-8 bg-ahm-orange" : "w-4 bg-ahm-teal/25"
              }`}
              data-testid={`cinematic-progress-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
